import { useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';

import { queryClient } from 'config/query-client';
import useTranslation from 'customHooks/translate';
import { getCurrencies } from 'services/collections';
import { addBankAccount, getBankById, updateBankAccount } from 'services/banks';
import { NewBankAccountError } from 'types';
import { BANK_DATA, CURRENCIES } from 'events/types';

import CustomDialog from './CustomDialog';

interface IProps {
  open: boolean;
  managerToUpdate?: any;
  handleClose: () => void;
  bankToEdit?: any;
}

const initialValues = {
  bankName: '',
  iban: '',
  address: '',
  correspondentBankAccount: '',
  swift: '',
  bankCode: '',
  currencyId: null,
};

const AddNewBankAccountForm = ({ open, handleClose, bankToEdit }: IProps) => {
  const translation = useTranslation();

  const [bankAccount, setBankAccount] = useState(initialValues);
  const [bankNameError, setBankNameError] = useState<string | null>(null);
  const [bankCodeError, setBankCodeError] = useState<string | null>(null);
  const [ibanError, setIbanError] = useState<string | null>(null);
  const [swiftError, setSwiftError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const setOnValueChange = (event, propName, isSelect = false) => {
    event.persist && event.persist();
    setBankAccount((prevState) => ({
      ...prevState,
      [propName]: isSelect ? event.value : event.target.value,
    }));

    if (propName === 'bankName') setBankNameError(null);
    if (propName === 'bankCode') setBankCodeError(null);
    if (propName === 'iban') setIbanError(null);
    if (propName === 'swift') setSwiftError(null);
  };

  useEffect(() => {
    if (!bankToEdit) {
      setBankAccount(initialValues);
      return;
    }
    getBankById(bankToEdit.id).then((response) => {
      setBankAccount(response.data.data);
    });
  }, [bankToEdit]);

  const handleSave = () => {
    const method = bankToEdit?.id ? updateBankAccount : addBankAccount;
    setLoading(true);
    method(bankAccount)
      .then(() => {
        message.success(`${translation.messages.successAdded}`);
        queryClient.invalidateQueries({
          queryKey: [BANK_DATA],
          refetchType: 'all',
        });
        setBankAccount(initialValues);

        if (currenciesQuery.data && currenciesQuery.data.length > 0) {
          setBankAccount((prevState) => ({
            ...initialValues,
            currencyId: currenciesQuery.data[0]?.value || null,
          }));
        }

        handleClose();
      })
      .catch((error: NewBankAccountError) => {
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        const errorMessageName = error.response?.data.errors?.BankName?.[0] as
          | string
          | undefined;

        const errorMessageCode = error.response?.data.errors?.BankCode?.[0] as
          | string
          | undefined;

        const errorMessageIban = error.response?.data.errors?.IBAN?.[0] as
          | string
          | undefined;

        const errorMessageSwift = error.response?.data.errors?.Swift?.[0] as
          | string
          | undefined;

        setBankNameError(errorMessageName || null);
        setBankCodeError(errorMessageCode || null);
        setIbanError(errorMessageIban || null);
        setSwiftError(errorMessageSwift || null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const currenciesQuery = useQuery({
    queryKey: [CURRENCIES],
    queryFn: getCurrencies,
    select: (response) =>
      response.data.data.map((d) => ({ value: d.currencyId, label: d.name })),
  });

  useEffect(() => {
    if (!bankToEdit && currenciesQuery.data) {
      setBankAccount((prevState) => ({
        ...prevState,
        currencyId: currenciesQuery.data[0]?.value || null,
      }));
    }
  }, [bankToEdit, currenciesQuery.data]);

  const isCurrencyAZN = bankAccount.currencyId === 'AZN';

  return (
    <CustomDialog
      isLoading={loading}
      open={open}
      handleClose={handleClose}
      onSubmit={handleSave}
      buttonTitle={
        bankToEdit?.id
          ? translation.bankAdd.UpdateButton
          : translation.bankAdd.AddButton
      }
      title={
        bankToEdit?.id
          ? translation.bankAdd.UpdateButton
          : translation.bankAdd.HeaderAddBank
      }
    >
      <form className="flex flex-col gap-[20px]">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
          <div className="flex flex-col gap-[12px]">
            <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.bankAdd.Name} *
            </label>
            <Input
              className="d-block"
              type="text"
              maxLength={29}
              onChange={(event) => setOnValueChange(event, 'bankName')}
              placeholder="123456"
              value={bankAccount.bankName || ''}
              required
              style={{
                borderColor: bankNameError ? 'red' : '',
              }}
            />
            {bankNameError && (
              <span className="text-[12px] text-red-600">{bankNameError}</span>
            )}
          </div>
          <div className="flex flex-col gap-[12px]">
            <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.bankAdd.Currency}
            </label>
            <Select
              classNamePrefix="react-select"
              options={currenciesQuery.data}
              value={currenciesQuery.data?.find(
                (currencyQuery) =>
                  currencyQuery.value === bankAccount.currencyId
              )}
              onChange={(event) => setOnValueChange(event, 'currencyId', true)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-[12px]">
          <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
            {translation.bankAdd.Account} *
          </label>
          <Input
            className="d-block"
            type="text"
            maxLength={29}
            onChange={(event) => setOnValueChange(event, 'iban')}
            placeholder="123456"
            value={bankAccount.iban || ''}
            required
            style={{
              borderColor: ibanError ? 'red' : '',
            }}
          />
          {ibanError && (
            <span className="text-[12px] text-red-600">{ibanError}</span>
          )}
        </div>
        <div className="flex flex-col gap-[12px]">
          <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
            {translation.bankAdd.CorrespondAccount}
          </label>
          <Input
            className="d-block"
            type="text"
            maxLength={29}
            onChange={(event) =>
              setOnValueChange(event, 'correspondentBankAccount')
            }
            placeholder="123456"
            value={bankAccount.correspondentBankAccount || ''}
          />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
          <div className="flex flex-col gap-[12px]">
            <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.bankAdd.Swift} *
            </label>
            <Input
              className="d-block"
              type="text"
              maxLength={29}
              onChange={(event) => setOnValueChange(event, 'swift')}
              placeholder="123456"
              value={bankAccount.swift || ''}
              required
              style={{
                borderColor: swiftError ? 'red' : '',
              }}
            />
            {swiftError && (
              <span className="text-[12px] text-red-600">{swiftError}</span>
            )}
          </div>
          <div className="flex flex-col gap-[12px]">
            <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.bankAdd.Code}
              {isCurrencyAZN ? ' *' : ''}
            </label>
            <Input
              className="d-block"
              type="text"
              maxLength={29}
              onChange={(event) => setOnValueChange(event, 'bankCode')}
              placeholder="123456"
              value={bankAccount.bankCode || ''}
              required
              style={{
                borderColor: bankCodeError ? 'red' : '',
              }}
            />
            {bankCodeError && (
              <span className="text-[12px] text-red-600">{bankCodeError}</span>
            )}
          </div>
        </div>
      </form>
    </CustomDialog>
  );
};

export default AddNewBankAccountForm;

const Input = styled.input`
  width: 100%;
  border-radius: 6px;
  padding: 12.7px 15px;
  border: 1px solid rgba(41, 45, 50, 0.25);
  :focus {
    outline: none !important;
    border: 2px solid #466afb;
    padding: 11.9px 14px;
  }
`;

const Select = styled(ReactSelect)`
  & .react-select__value-container {
    padding: 8.7px 16px;
  }
`;
